import { logger } from "../../utils/logger";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Container, Typography, Box, Card, CardContent, CardActions, 
  Button, Grid, Paper, Chip, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, MenuItem, FormControl, InputLabel, 
  Select, CircularProgress, Alert, Snackbar, Divider, Stack,
  List, ListItem, ListItemText, ListItemIcon, Tooltip, Badge,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, IconButton, LinearProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Refresh as RefreshIcon, Delete as DeleteIcon, 
  Edit as EditIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon,
  Sync as SyncIcon, Calculate as CalculateIcon, ShoppingCart as ShopifyIcon,
  Payment as StripeIcon, Business as BusinessIcon, Store as StoreIcon,
  Settings as SettingsIcon, OpenInNew as OpenInNewIcon, Info as InfoIcon,
  Receipt as ReceiptIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon, Assessment as AssessmentIcon, AccountBalance as QuickBooksIcon
} from '@mui/icons-material';
import platformService from '../../services/platformService';
import { companyService } from '../../services/companyService';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { ErrorDisplay } from '../../components/common/ErrorDisplay';
import CompanySelector from '../../components/CompanySelector';
import { PLATFORM_OPTIONS } from '../../utils/platforms';
import {
  handleQuickBooksImport as sharedHandleQuickBooksImport,
} from '../../utils/quickbooksImport';
import { handleShopifyImport as sharedHandleShopifyImport } from '../../utils/shopifyImport';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { mapErrorToUserMessage } from '../../utils/errorMapping';
import { ImportDateRangeControls } from '../../components/ImportDateRangeControls';
import { useSharedImportDateRange } from '../../hooks/useSharedImportDateRange';
import { sanitizeImportDateRange } from '../../utils/importDateRange';

const PlatformIntegrations = () => {
  const [platforms, setPlatforms] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companiesError, setCompaniesError] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingIntegrations, setSyncingIntegrations] = useState(new Set()); // Track which integrations are syncing
  const [syncStatus, setSyncStatus] = useState(null); // Show sync status messages
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [formData, setFormData] = useState({
    company_id: '',
    platform_id: '',
    api_key: '',
    api_secret: '',
    store_id: '',
    access_token: '',
    refresh_token: '',
    webhook_secret: '',
    is_active: true,
    settings: {}
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Transaction viewing state
  const [transactionsDialog, setTransactionsDialog] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [integrationTransactions, setIntegrationTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Add state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState(null);

  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [shopifyStore, setShopifyStore] = useState('');
  // Which one-click connections this service has set up (null until loaded)
  const [oauthStatus, setOauthStatus] = useState(null);
  useEffect(() => {
    api.get('/api/v1/platforms/oauth-status').then((r) => setOauthStatus(r.data)).catch(() => setOauthStatus(null));
  }, []);
  const qbReady = !oauthStatus || oauthStatus.quickbooks?.configured !== false;
  const shopifyReady = !oauthStatus || oauthStatus.shopify?.configured !== false;
  const missingSettings = [...(oauthStatus?.quickbooks?.missing || []), ...(oauthStatus?.shopify?.missing || [])];
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null); // 'quickbooks', 'shopify', or 'csv'
  const [uploadedFile, setUploadedFile] = useState(null);
  const {
    range: importDateRange,
    setRange: setImportDateRange,
    error: importDateRangeError
  } = useSharedImportDateRange();

  const breadcrumbs = [
    { label: 'Platform Integrations' }
  ];

  // Handlers for quick action buttons
  const handleShopifyConnect = async () => {
    setImporting(true);
    setImportError(null);
    try {
      if (importDateRangeError) {
        setImportError(importDateRangeError);
        return;
      }
      if (!selectedCompanyId) {
        setImportError('Please select a company');
        return;
      }
      if (!shopifyStore) {
        setImportError('Please enter your Shopify store name');
        return;
      }
      
      // CRITICAL FIX: Ensure we're passing an ID, not an object
      const actualId = typeof selectedCompanyId === 'object' && selectedCompanyId !== null 
        ? selectedCompanyId.id 
        : selectedCompanyId;
      
      await sharedHandleShopifyImport({
        source: 'platform-integrations',
        user,
        setUploadError: setImportError,
        setImporting,
        navigate,
        companyId: actualId,
        shop: shopifyStore,
        dateRange: importDateRange
      });
      setIntegrationDialogOpen(false);
      setShopifyStore('');
      refreshIntegrations();
    } catch (err) {
      setImportError(getErrorMessage(err));
    } finally {
      setImporting(false);
    }
  };

  const handleCSVUpload = async () => {
    setImportError(null);
    if (importDateRangeError) {
      setImportError(importDateRangeError);
      return;
    }
    if (!uploadedFile) {
      setImportError('Please select a CSV file to upload');
      return;
    }

    // Get company ID - use first company if none selected
    let companyId = selectedCompanyId;
    if (!companyId && companies.length > 0) {
      companyId = companies[0].id;
      setSelectedCompanyId(companyId);
    }
    
    if (!companyId) {
      setImportError('No company available. Please create a company first.');
      return;
    }

    // CRITICAL FIX: Ensure we're passing an ID, not an object
    const actualId = typeof companyId === 'object' && companyId !== null 
      ? companyId.id 
      : companyId;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('company_id', actualId);
      const { startDate, endDate } = sanitizeImportDateRange(importDateRange);
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);
      
      await platformService.uploadCSV(formData);
      setSuccess('CSV file uploaded successfully');
      setIntegrationDialogOpen(false);
      setUploadedFile(null);
      refreshIntegrations();
    } catch (err) {
      setImportError(getErrorMessage(err));
    } finally {
      setImporting(false);
    }
  };

  // Helper to ensure error is always a string
  const getErrorMessage = useCallback((err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (err?.message) return String(err.message);
    return 'An error occurred';
  }, []);

  const loadCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    setCompaniesError(null);
    try {
      const companiesData = await companyService.listCompanies();
      setCompanies(companiesData);
      let resolvedId = null;
      setSelectedCompanyId((prev) => {
        if (prev && companiesData.some((c) => c.id === prev)) {
          resolvedId = prev;
          return prev;
        }
        resolvedId = companiesData.length > 0 ? companiesData[0].id : null;
        return resolvedId;
      });
      if (resolvedId !== null) {
        setFormData((prev) => ({
          ...prev,
          company_id: resolvedId,
        }));
      }
      return companiesData;
    } catch (err) {
      const message = getErrorMessage(err);
      setCompaniesError(message);
      setCompanies([]);
      setSelectedCompanyId(null);
      throw err;
    } finally {
      setCompaniesLoading(false);
    }
  }, [getErrorMessage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const companiesData = await loadCompanies();
        const fallbackId =
          (selectedCompanyId && companiesData.some((c) => c.id === selectedCompanyId))
            ? selectedCompanyId
            : (companiesData[0]?.id ?? null);
        if (fallbackId) {
          const integrationsData = await platformService.listIntegrations(fallbackId);
          setIntegrations(integrationsData);
        } else {
          setIntegrations([]);
        }
      } catch (err) {
        setError(getErrorMessage(err));
        logger.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [loadCompanies, selectedCompanyId, getErrorMessage]);

  const handleOpenDialog = (integration = null) => {
    if (integration) {
      setEditingIntegration(integration);
      setFormData({
        company_id: integration.company_id,
        platform_id: integration.platform_id,
        api_key: integration.api_key || '',
        api_secret: integration.api_secret || '',
        store_id: integration.store_id || '',
        access_token: integration.access_token || '',
        refresh_token: integration.refresh_token || '',
        webhook_secret: integration.webhook_secret || '',
        is_active: integration.is_active,
        settings: integration.settings || {}
      });
    } else {
      setEditingIntegration(null);
      setFormData({
        company_id: selectedCompanyId || (companies.length > 0 ? companies[0].id : ''),
        platform_id: '',
        api_key: '',
        api_secret: '',
        store_id: '',
        access_token: '',
        refresh_token: '',
        webhook_secret: '',
        is_active: true,
        settings: {}
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingIntegration(null);
    setFormData({
      company_id: selectedCompanyId || (companies.length > 0 ? companies[0].id : ''),
      platform_id: '',
      api_key: '',
      api_secret: '',
      store_id: '',
      access_token: '',
      refresh_token: '',
      webhook_secret: '',
      is_active: true,
      settings: {}
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'company_id' ? Number(value) : value
    });
  };

  // Add a helper to check for existing integration for a platform
  const hasExistingIntegration = (platformId) => {
    return integrations.some(
      (integration) =>
        integration.company_id === formData.company_id &&
        (integration.platform_id === platformId ||
          (typeof integration.platform === 'object' && integration.platform?.id === platformId) ||
          (typeof integration.platform === 'object' && integration.platform?.name?.toLowerCase() === PLATFORM_OPTIONS.find(p => p.id === platformId)?.name?.toLowerCase())
        )
    );
  };

  const handleImport = async () => {
    setImporting(true);
    setImportError(null);
    try {
      if (!formData.company_id) {
        setImportError('Please select a company');
        setImporting(false);
        return;
      }
      if (!formData.platform_id) {
        setImportError('Please select a platform');
        setImporting(false);
        return;
      }
      // Prevent duplicate QuickBooks or Shopify integrations
      const platformName = PLATFORM_OPTIONS.find(p => p.id === formData.platform_id)?.name?.toLowerCase();
      if ((platformName === 'quickbooks' || platformName === 'shopify') && hasExistingIntegration(formData.platform_id)) {
        setImportError(`A ${platformName.charAt(0).toUpperCase() + platformName.slice(1)} integration already exists for this company. Please edit or remove the existing integration before creating a new one.`);
        setImporting(false);
        return;
      }
      // In the validation logic, only require Store ID if platform is BigCommerce (not Shopify)
      const isBigCommerce = formData.platform_id.toLowerCase() === 'bigcommerce';
      const errors = {};
      if (isBigCommerce && !formData.store_id) {
        errors.store_id = 'Please enter Store ID';
      }
      if (Object.keys(errors).length > 0) {
        setImportError(Object.values(errors)[0]);
        setImporting(false);
        return;
      }
      if (formData.platform_id === 'quickbooks') {
        if (!user || !user.id || !user.email) {
          setImportError('You must be logged in to connect QuickBooks.');
          setImporting(false);
          logger.error('[PlatformIntegrations] User not logged in or missing id/email:', user);
          return;
        }
        if (importDateRangeError) {
          setImportError(importDateRangeError);
          setImporting(false);
          return;
        }
        await sharedHandleQuickBooksImport({
          source: 'platform-integrations',
          user: {
            id: user.id,
            email: user.email,
            company_id: formData.company_id
          },
          setUploadError: setImportError,
          setImporting,
          navigate,
          companyId: formData.company_id,
          dateRange: importDateRange
        });
        return;
      } else if (formData.platform_id === 'shopify') {
        if (!user || !user.id || !user.email) {
          setImportError('You must be logged in to connect Shopify.');
          setImporting(false);
          logger.error('[PlatformIntegrations] User not logged in or missing id/email:', user);
          return;
        }
        if (!shopifyStore) {
          setImportError('Please enter your Shopify store name.');
          setImporting(false);
          return;
        }
        if (importDateRangeError) {
          setImportError(importDateRangeError);
          setImporting(false);
          return;
        }
        await sharedHandleShopifyImport({
          source: 'platform-integrations',
          user: {
            id: user.id,
            email: user.email,
            company_id: formData.company_id
          },
          setUploadError: setImportError,
          setImporting,
          navigate,
          companyId: formData.company_id,
          shop: shopifyStore,
          dateRange: importDateRange
        });
        return;
      } else {
        setImportError('Platform not supported for import.');
        setImporting(false);
        return;
      }
    } catch (err) {
      logger.error('[PlatformIntegrations] Import error:', err);
      if (err.response && err.response.data && err.response.data.detail) {
        setImportError(err.response.data.detail);
      } else {
        setImportError('An unexpected error occurred while connecting to the platform.');
      }
    } finally {
      setImporting(false);
    }
  };

  // Update error handling for verify and sync
  const handleVerifyCredentials = async (integrationId) => {
    try {
      setLoading(true);
      const result = await platformService.verifyCredentials(integrationId);
      if (result.success) {
        setSuccess('Credentials are valid!');
      } else {
        setError(result.message || 'Invalid credentials. Please check your settings and try again.');
      }
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTransactions = async (integrationId) => {
    try {
      if (importDateRangeError) {
        setError(importDateRangeError);
        return;
      }
      const { startDate: startISO, endDate: endISO } = sanitizeImportDateRange(importDateRange);
      // Add integration to syncing set
      setSyncingIntegrations(prev => new Set(prev).add(integrationId));
      setSyncStatus(`Syncing integration ${integrationId}...`);
      
      // Start background sync
      logger.log(`[Individual Sync] Integration ${integrationId}: Sending dates: startDate=${startISO}, endDate=${endISO}`);
      await platformService.syncTransactions(integrationId, { 
        startDate: startISO,
        endDate: endISO,
        background: true 
      });
      
      logger.log(`[Individual Sync] Integration ${integrationId}: Sync request sent successfully`);
      
      setSyncStatus(`Sync started for integration ${integrationId}. This may take a few minutes.`);
      
      // Wait a bit then refresh to show any new transactions
      setTimeout(async () => {
        try {
          await refreshIntegrations();
          setSyncStatus(`Sync completed for integration ${integrationId}.`);
          
          // Clear status after a few seconds
          setTimeout(() => {
            setSyncStatus(null);
          }, 3000);
        } catch (refreshError) {
          logger.warn('Failed to refresh after sync:', refreshError);
        }
        
        // Remove from syncing set
        setSyncingIntegrations(prev => {
          const newSet = new Set(prev);
          newSet.delete(integrationId);
          return newSet;
        });
      }, 3000);
      
    } catch (err) {
      logger.error(`[Individual Sync] Integration ${integrationId}: Sync failed:`, err);
      logger.error(`[Individual Sync] Integration ${integrationId}: Error details:`, err.response?.data);
      setError(mapErrorToUserMessage(err));
      
      // Remove from syncing set on error
      setSyncingIntegrations(prev => {
        const newSet = new Set(prev);
        newSet.delete(integrationId);
        return newSet;
      });
      setSyncStatus(null);
    }
  };

  const handleSyncAllTransactions = async () => {
    try {
      if (importDateRangeError) {
        setError(importDateRangeError);
        setSyncStatus(null);
        return;
      }
      const { startDate: startISO, endDate: endISO } = sanitizeImportDateRange(importDateRange);
      setSyncStatus('Starting sync for all active integrations...');
      logger.log('[UI] Starting sync for all active integrations');
      
      // Get all active integrations
      const activeIntegrations = integrations.filter(integration => integration.is_active);
      logger.log(`[UI] Found ${activeIntegrations.length} active integrations`);
      
      if (activeIntegrations.length === 0) {
        setSyncStatus('No active integrations found to sync.');
        setTimeout(() => setSyncStatus(null), 3000);
        return;
      }
      
      // Add all integrations to syncing set
      const syncingIds = new Set(activeIntegrations.map(i => i.id));
      setSyncingIntegrations(syncingIds);
      
      // Sync all active integrations
      for (const integration of activeIntegrations) {
        try {
          logger.log(`[Sync All] Syncing integration ${integration.id} (${integration.platform?.name}) with dates: startDate=${startISO}, endDate=${endISO}`);
          await platformService.syncTransactions(integration.id, { 
            startDate: startISO,
            endDate: endISO,
            background: true 
          });
        } catch (syncError) {
          logger.warn(`[UI] Failed to sync integration ${integration.id}:`, syncError);
          // Continue with other integrations even if one fails
        }
      }
      
      setSyncStatus(`Sync initiated for ${activeIntegrations.length} active integrations. This may take a few minutes to complete.`);
      
      // Refresh integrations after a delay
      setTimeout(async () => {
        try {
          await refreshIntegrations();
          setSyncStatus(`Sync completed for all ${activeIntegrations.length} integrations.`);
          
          // Clear status after a few seconds
          setTimeout(() => {
            setSyncStatus(null);
          }, 3000);
        } catch (refreshError) {
          logger.warn('Failed to refresh after sync all:', refreshError);
        }
        
        // Clear syncing set
        setSyncingIntegrations(new Set());
      }, 5000);
      
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      logger.error(err);
      setSyncingIntegrations(new Set());
      setSyncStatus(null);
    }
  };

  const handleCalculateTax = async (integrationId) => {
    try {
      setLoading(true);
      await platformService.calculateTax(integrationId, { background: true });
      setSuccess('Tax calculation started in background. It may take a few minutes to complete.');
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIntegration = (integrationId) => {
    setIntegrationToDelete(integrationId);
    setDeleteDialogOpen(true);
  };
  const confirmDeleteIntegration = async () => {
    if (!integrationToDelete) return;
    try {
      setLoading(true);
      await platformService.deleteIntegration(integrationToDelete);
      setSuccess('Integration deleted successfully.');
      refreshIntegrations();
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      logger.error(err);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setIntegrationToDelete(null);
    }
  };
  const cancelDeleteIntegration = () => {
    setDeleteDialogOpen(false);
    setIntegrationToDelete(null);
  };

  const refreshIntegrations = async (companyIdParam) => {
    try {
      setLoading(true);
      const effectiveId = companyIdParam ?? selectedCompanyId;
      let actualId = null;
      if (typeof effectiveId === 'number') {
        actualId = effectiveId;
      } else if (typeof effectiveId === 'string' && effectiveId.trim() !== '') {
        const parsed = Number(effectiveId);
        actualId = Number.isFinite(parsed) ? parsed : null;
      }
      const integrationsData = await platformService.listIntegrations(
        actualId ?? undefined
      );
      setIntegrations(integrationsData);
    } catch (err) {
      setError(getErrorMessage(err));
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTransactions = async (integration) => {
    try {
      setSelectedIntegration(integration);
      setLoadingTransactions(true);
      setTransactionsDialog(true);
      
      const transactions = await platformService.listTransactions(integration.id, { limit: 50 });
      setIntegrationTransactions(transactions);
    } catch (err) {
      setError('Failed to load transactions');
      logger.error('Error loading transactions:', err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleCloseTransactions = () => {
    setTransactionsDialog(false);
    setSelectedIntegration(null);
    setIntegrationTransactions([]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const getTransactionStatusColor = (status) => {
    switch (status) {
      case 'TAX_CALCULATED':
        return 'success';
      case 'PENDING_TAX_CALCULATION':
        return 'warning';
      case 'IMPORTED':
        return 'info';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Fix getPlatformName to use integration.platform?.name if available
  const getPlatformName = (platformName, integration) => {
    // First try to use the platform object from the integration
    if (integration && integration.platform && integration.platform.name) {
      return integration.platform.name;
    }
    // If platformName is passed directly, use it
    if (platformName) {
      return platformName;
    }
    // Fallback to platforms lookup
    const platform = platforms.find(p => p.name?.toLowerCase() === platformName?.toLowerCase());
    return platform ? platform.name : 'Unknown';
  };

  // Fix getIntegrationStatus to show 'Connected' if access_token is present and is_active
  const getIntegrationStatus = (integration) => {
    if (!integration.is_active) {
      return { color: 'error', label: 'Inactive' };
    }
    if (integration.access_token) {
      return { color: 'success', label: 'Connected' };
    }
    return { color: 'warning', label: 'Not Connected' };
  };

  const getCompanyName = (companyId, integration) => {
    // Always show the GoTruTax user's company name, not the platform company
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown Company';
  };

  const getPlatformIcon = (platformName) => {
    if (!platformName) return <StoreIcon />;
    
    switch (platformName.toLowerCase()) {
      case 'shopify':
        return <ShopifyIcon />;
      case 'stripe':
        return <StripeIcon />;
      case 'quickbooks':
        return <QuickBooksIcon />;
      default:
        return <StoreIcon />;
    }
  };

  const columns = [
    { 
      field: 'company', 
      headerName: 'Company', 
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2">
            {getCompanyName(params.row.company_id, params.row)}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'platform', 
      headerName: 'Platform', 
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getPlatformIcon(params.row.platform_name)}
          <Typography variant="body2" sx={{ ml: 1 }}>
            {getPlatformName(params.row.platform_name, params.row)}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'store_id', 
      headerName: 'Store ID', 
      width: 250,
      flex: 1,
      renderCell: (params) => {
        // For QuickBooks integrations, show the company name and realm ID
        if (params.row.platform_name?.toLowerCase() === 'quickbooks') {
          // Show company name and realm ID from settings
          const companyName = params.row.settings?.company_name;
          const realmId = params.row.settings?.realm_id || params.row.store_id;
          
          if (companyName && realmId) {
            return (
              <Tooltip title={`QuickBooks Company: ${companyName} (Realm ID: ${realmId})`}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    fontWeight: 500,
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: 1.2
                  }}
                >
                  {companyName} ({realmId})
                </Typography>
              </Tooltip>
            );
          } else if (companyName) {
            return (
              <Tooltip title={`QuickBooks Company: ${companyName}`}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    fontWeight: 500,
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: 1.2
                  }}
                >
                  {companyName}
                </Typography>
              </Tooltip>
            );
          } else if (realmId) {
            return (
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: 'monospace', 
                  fontWeight: 500,
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                  lineHeight: 1.2
                }}
              >
                QB Company {realmId}
              </Typography>
            );
          }
        }
        // For other integrations, show the store_id
        return (
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: 'monospace',
              wordWrap: 'break-word',
              whiteSpace: 'normal',
              lineHeight: 1.2
            }}
          >
            {params.row.store_id || 'Not set'}
          </Typography>
        );
      }
    },
    { 
      field: 'transactions', 
      headerName: 'Transactions', 
      width: 130,
      renderCell: (params) => {
        const stats = params.row.transaction_stats;
        if (!stats || stats.total_transactions === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              No transactions
            </Typography>
          );
        }
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {stats.total_transactions} total
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.tax_calculated} calculated
            </Typography>
          </Box>
        );
      }
    },
    { 
      field: 'tax_status', 
      headerName: 'Tax Calculation', 
      width: 150,
      renderCell: (params) => {
        const stats = params.row.transaction_stats;
        if (!stats || stats.total_transactions === 0) {
          return (
            <Chip 
              label="No Data" 
              color="default" 
              size="small" 
            />
          );
        }
        
        const percentage = Math.round((stats.tax_calculated / stats.total_transactions) * 100);
        const pendingCount = stats.pending_tax_calculation || 0;
        
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" sx={{ minWidth: 40 }}>
                {percentage}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={percentage} 
                sx={{ flexGrow: 1, ml: 1, height: 4 }}
                color={percentage === 100 ? 'success' : 'warning'}
              />
            </Box>
            {pendingCount > 0 && (
              <Typography variant="caption" color="warning.main">
                {pendingCount} pending
              </Typography>
            )}
          </Box>
        );
      }
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 110,
      renderCell: (params) => {
        const status = getIntegrationStatus(params.row);
        return (
          <Chip 
            icon={status.color === 'success' ? <CheckCircleIcon /> : <ErrorIcon />} 
            label={status.label} 
            color={status.color} 
            size="small" 
          />
        );
      }
    },
    { 
      field: 'last_sync_at', 
      headerName: 'Last Sync', 
      width: 140,
      valueGetter: (params) => params.row.last_sync_at ? new Date(params.row.last_sync_at).toLocaleString() : 'Never'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 500,
      renderCell: (params) => {
        const stats = params.row.transaction_stats;
        const hasTransactions = stats && stats.total_transactions > 0;
        
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
            <Tooltip title="Edit Integration">
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => handleOpenDialog(params.row)}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Edit
              </Button>
            </Tooltip>
            <Tooltip title="Test Connection">
              <Button
                size="small"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleVerifyCredentials(params.row.id)}
                color="info"
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Test
              </Button>
            </Tooltip>
            {hasTransactions && (
              <Tooltip title="View Transactions">
                <Button
                  size="small"
                  startIcon={<ReceiptIcon />}
                  onClick={() => handleViewTransactions(params.row)}
                  color="secondary"
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  Transactions
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Sync Transactions">
              <Button
                size="small"
                startIcon={syncingIntegrations.has(params.row.id) ? <CircularProgress size={12} color="inherit" /> : <SyncIcon />}
                onClick={() => handleSyncTransactions(params.row.id)}
                disabled={syncingIntegrations.has(params.row.id)}
                color="success"
                sx={{ minWidth: 'auto', px: 1 }}
              >
                {syncingIntegrations.has(params.row.id) ? 'Syncing...' : 'Sync'}
              </Button>
            </Tooltip>
            <Tooltip title="Delete Integration">
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteIntegration(params.row.id)}
                color="error"
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Del
              </Button>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleCompanyChange = async (newCompanyId) => {
    setSelectedCompanyId(newCompanyId);
    setFormData((prev) => ({
      ...prev,
      company_id: newCompanyId,
    }));
    await refreshIntegrations(newCompanyId);
  };

  return (
    <Container maxWidth="xl" className="gt-page gt-page-wide">
      <PageHeader 
        title="Platform Integrations" 
        breadcrumbs={breadcrumbs}
        description="Connect Shopify, QuickBooks or Stripe to sync transactions and calculate tax automatically."
      />
      
      <Box sx={{ mb: 3 }}>
        
        {/* Sync Status Display */}
        {syncStatus && (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2">
              {syncStatus}
            </Typography>
          </Alert>
        )}
        
        {(!qbReady || !shopifyReady) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {[!qbReady && 'QuickBooks', !shopifyReady && 'Shopify'].filter(Boolean).join(' and ')} one-click connections are coming soon.
            Until then, import your sales with a CSV upload or send them through the API.
            {missingSettings.length > 0 && (
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                Admin note: set {missingSettings.join(', ')} (plus the redirect URL) to enable these.
              </Typography>
            )}
          </Alert>
        )}

        {/* Quick Actions */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ maxWidth: 560, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Import sync date range
            </Typography>
            <ImportDateRangeControls
              range={importDateRange}
              onChange={setImportDateRange}
              helperText="Leave the dates blank to import the last 365 days."
              disabled={importing}
              errorText={importDateRangeError}
            />
          </Box>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Button 
              variant="outlined" 
              startIcon={<BusinessIcon />}
              onClick={() => {
                if (importDateRangeError) {
                  setError(importDateRangeError);
                  return;
                }
                sharedHandleQuickBooksImport({
                  source: 'platform-integrations',
                  user,
                  setUploadError: setImportError,
                  setImporting,
                  navigate,
                  companyId: selectedCompanyId,
                  dateRange: importDateRange
                });
              }}
              disabled={importing || !qbReady}
              color="primary"
            >
              {qbReady ? 'Connect QuickBooks' : 'QuickBooks (coming soon)'}
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<ShopifyIcon />}
              onClick={() => {
                if (importDateRangeError) {
                  setError(importDateRangeError);
                  return;
                }
                setSelectedPlatform('shopify');
                setIntegrationDialogOpen(true);
              }}
              disabled={importing || !shopifyReady}
              color="secondary"
            >
              {shopifyReady ? 'Connect Shopify' : 'Shopify (coming soon)'}
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedPlatform('csv');
                setIntegrationDialogOpen(true);
              }}
              disabled={importing}
              color="info"
            >
              Upload CSV
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />} 
              onClick={refreshIntegrations}
            >
              Refresh
            </Button>
            <Button 
              variant="contained" 
              startIcon={syncingIntegrations.size > 0 ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />} 
              onClick={handleSyncAllTransactions}
              disabled={syncingIntegrations.size > 0}
              color="success"
            >
              {syncingIntegrations.size > 0 ? 'Syncing...' : 'Sync All Transactions'}
            </Button>
          </Stack>
        </Box>

        {/* Company Selector */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CompanySelector
            selectedCompanyId={selectedCompanyId || 0}
            onCompanyChange={handleCompanyChange}
            label="Company"
            companies={companies}
            loading={companiesLoading}
            error={companiesError}
            onRefresh={loadCompanies}
            includeAllOption={false}
            disabled={companiesLoading || companies.length <= 1}
          />
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StoreIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{integrations.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Integrations
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {integrations.filter(i => i.is_active).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Integrations
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {integrations.reduce((sum, i) => sum + (i.transaction_stats?.total_transactions || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Transactions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssessmentIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {integrations.reduce((sum, i) => sum + (i.transaction_stats?.tax_calculated || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tax Calculated
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Move credentials success message to top of page as Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper elevation={1}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>}
        
        {integrations.length === 0 && !loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <StoreIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No integrations configured
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Connect your first e-commerce platform to start syncing transactions
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpenDialog()}
            >
              Add Your First Integration
            </Button>
          </Box>
        ) : (
          <DataGrid
            rows={integrations}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{ 
              minHeight: 400,
              '& .MuiDataGrid-cell': {
                padding: '8px 4px',
                fontSize: '0.875rem'
              },
              '& .MuiDataGrid-columnHeader': {
                padding: '8px 4px',
                fontSize: '0.875rem'
              }
            }}
          />
        )}
      </Paper>

      {/* Edit Integration Dialog - Only for existing integrations */}
      {editingIntegration && (
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StoreIcon sx={{ mr: 1 }} />
              Edit Integration
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Currently editing existing integrations is limited. For new connections, please use the Complete Tax View.
            </Alert>
            {/* Basic editing form could go here if needed */}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Transactions Dialog */}
      <Dialog open={transactionsDialog} onClose={handleCloseTransactions} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ mr: 1 }} />
            Transactions - {selectedIntegration?.platform?.name} ({selectedIntegration?.store_id})
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingTransactions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Transaction Stats Summary */}
              {selectedIntegration?.transaction_stats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h5">{selectedIntegration.transaction_stats.total_transactions}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Transactions</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h5" color="success.main">
                          {selectedIntegration.transaction_stats.tax_calculated}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Tax Calculated</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h5" color="warning.main">
                          {selectedIntegration.transaction_stats.pending_tax_calculation}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Pending Tax</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h5">
                          {selectedIntegration.transaction_stats.total_transactions > 0 
                            ? Math.round((selectedIntegration.transaction_stats.tax_calculated / selectedIntegration.transaction_stats.total_transactions) * 100)
                            : 0}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Completion Rate</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Transactions Table */}
              {integrationTransactions.length === 0 ? (
                <Alert severity="info">
                  No transactions found for this integration. Try syncing with the platform to import transactions.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Transaction ID</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Tax Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Customer</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {integrationTransactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {transaction.platform_transaction_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {transaction.transaction_date 
                              ? new Date(transaction.transaction_date).toLocaleDateString()
                              : 'N/A'
                            }
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatCurrency(transaction.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              color={transaction.tax_amount > 0 ? 'success.main' : 'text.secondary'}
                            >
                              {formatCurrency(transaction.tax_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.status.replace('_', ' ')}
                              color={getTransactionStatusColor(transaction.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {transaction.customer_name || transaction.customer_email || 'Unknown'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransactions}>Close</Button>
          {selectedIntegration && (
            <>
              <Button
                variant="outlined"
                startIcon={syncingIntegrations.has(selectedIntegration.id) ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
                onClick={async () => {
                  await handleSyncTransactions(selectedIntegration.id);
                  // After sync, reload transactions for the dialog
                  setTimeout(async () => {
                    setLoadingTransactions(true);
                    const transactions = await platformService.listTransactions(selectedIntegration.id, { limit: 50 });
                    setIntegrationTransactions(transactions);
                    setLoadingTransactions(false);
                  }, 3000);
                }}
                disabled={syncingIntegrations.has(selectedIntegration.id)}
                color="success"
              >
                {syncingIntegrations.has(selectedIntegration.id) ? 'Syncing...' : 'Sync New Transactions'}
              </Button>
              <Button
                variant="contained"
                startIcon={<CalculateIcon />}
                onClick={() => {
                  handleCalculateTax(selectedIntegration.id);
                  handleCloseTransactions();
                }}
                color="primary"
              >
                Calculate Tax
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbars for success and error messages */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={8000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity="error" 
          sx={{ 
            maxWidth: '600px',
            '& .MuiAlert-message': {
              whiteSpace: 'pre-line'
            }
          }}
        >
          {typeof error === 'string' ? error : error?.message || 'An error occurred'}
        </Alert>
      </Snackbar>

      {/* Integration Dialog (Shopify/CSV) */}
      <Dialog 
        open={integrationDialogOpen} 
        onClose={() => {
          setIntegrationDialogOpen(false);
          setImportError(null);
          setShopifyStore('');
          setUploadedFile(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedPlatform === 'shopify' && 'Connect Shopify Store'}
          {selectedPlatform === 'csv' && 'Upload CSV File'}
        </DialogTitle>
        <DialogContent>
          {importError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {importError}
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Import for Company
            </Typography>
            <CompanySelector
              selectedCompanyId={selectedCompanyId || 0}
              onCompanyChange={handleCompanyChange}
              disabled={companiesLoading || companies.length <= 1}
              includeAllOption={false}
              companies={companies}
              loading={companiesLoading}
              error={companiesError}
              onRefresh={loadCompanies}
            />
          </Box>
          
          {selectedPlatform === 'shopify' && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your Shopify store name (e.g., mystore.myshopify.com)
              </Typography>
              <TextField
                fullWidth
                label="Shopify Store URL"
                value={shopifyStore}
                onChange={(e) => setShopifyStore(e.target.value)}
                placeholder="mystore.myshopify.com"
                disabled={importing}
              />
            </Box>
          )}
          
          {selectedPlatform === 'csv' && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a CSV file containing your transactions
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                disabled={importing}
              >
                {uploadedFile ? uploadedFile.name : 'Choose CSV File'}
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={(e) => setUploadedFile(e.target.files[0])}
                />
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setIntegrationDialogOpen(false);
              setImportError(null);
              setShopifyStore('');
              setUploadedFile(null);
            }}
            disabled={importing}
          >
            Cancel
          </Button>
          {selectedPlatform === 'shopify' && (
            <Button 
              onClick={handleShopifyConnect} 
              variant="contained" 
              disabled={importing || !shopifyStore}
              startIcon={importing ? <CircularProgress size={16} /> : <ShopifyIcon />}
            >
              {importing ? 'Connecting...' : 'Connect'}
            </Button>
          )}
          {selectedPlatform === 'csv' && (
            <Button 
              onClick={handleCSVUpload} 
              variant="contained" 
              disabled={importing || !uploadedFile}
              startIcon={importing ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {importing ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={cancelDeleteIntegration}>
        <DialogTitle>Delete Integration</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this integration? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteIntegration}>Cancel</Button>
          <Button onClick={confirmDeleteIntegration} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlatformIntegrations; 
